import { useMemo, useEffect, useCallback } from "react";

const useBillingCalculation = ({ rooms, values, taxConfig, setValues }) => {
    const percentOf = useCallback((p, base) => (Number(p || 0) * Number(base || 0)) / 100, []);

    // Computed rooms with per-room calculations
    const computedRooms = useMemo(() => {
        return rooms.map((room) => {
            // Determine facility days
            const facilityFrom = room.extracheck_in_date || values.checkin_date;
            const facilityTo = room.extracheck_out_date || values.checkout_date;

            let facilityDays = 1;
            if (facilityFrom && facilityTo) {
                const diff = Math.ceil(
                    (new Date(facilityTo) - new Date(facilityFrom)) / (1000 * 60 * 60 * 24)
                );
                facilityDays = diff > 0 ? diff : 1;
            }

            const extra_bed_charge = Number(room.extra_bed || 0) * Number(room.extra_bed_charge || 0) * facilityDays;
            const extra_person_charge = Number(room.extra_person || 0) * Number(room.extra_person_charge || 0) * facilityDays;
            const extra_child_charge = Number(room.extra_child || 0) * (Number(room.extra_child_charge || 0)) * facilityDays;

            const extra_room_total = extra_bed_charge + extra_person_charge + extra_child_charge;

            const room_rate = Number(room.room_rate || 0);
            const room_rate_totle = Number((room_rate * facilityDays) || 0);

            const complementary_price = Number(room.complementary_price || 0);
            const complementary_price_type = room.complementary_price_type || "";
            const complementary_total = complementary_price_type === "DAY_WISE" ? complementary_price * facilityDays : complementary_price;

            // ✅ FIX 1: Apply room_offer% ONLY on the base room rate total, not the extras
            const room_offer_pct = Number(room.room_offer || 0);
            const room_offer_amt = (room_rate_totle * room_offer_pct) / 100;

            // ✅ Subtotal calculation adjusted to subtract the localized offer safely
            const subtotal = room_rate_totle + extra_room_total + complementary_total;
            const room_total = (room_rate_totle - room_offer_amt) + extra_room_total + complementary_total;

            return {
                ...room,
                room_rate_totle,
                complementary_total,
                room_offer_amt,
                facilityDays,
                extra_bed_charge,
                extra_person_charge,
                extra_child_charge,
                extra_room_total,
                subtotal, // Keeps raw sum for UI display elements if needed
                room_total,
            };
        });
    }, [rooms, values.checkin_date, values.checkout_date]);

    const billingDeps = useMemo(() => ({
        roomsSum: computedRooms.reduce((sum, r) => sum + (Number(r.room_total) || 0), 0),
        commission: values.commission_percent,
        discount: values.discount,
    }), [computedRooms, values.commission_percent, values.discount]);

    const billingRates = useMemo(() => {
        if (rooms.length === 0) {
            return {
                baseRent: 0,
                extras: 0,
                commission_amount: 0,
                tax: 0,
                serviceCharge: 0,
                discountAmount: 0,
                total: 0,
                RoomBookingCharge: 0,
                RemainingAmount: 0
            };
        }

        const baseRent = billingDeps.roomsSum;

        const taxRate = Number(taxConfig?.total_tax_rate ?? 0);
        const total_tax_rate = Number(taxConfig?.total_tax_rate ?? 0);
        const service_charge = Number(taxConfig?.service_charge ?? 0);
        const serviceRate = Number(taxConfig?.service_charge ?? 0);

        const commission_amount = percentOf(billingDeps.commission, baseRent);
        const discountAmount = percentOf(billingDeps.discount, baseRent);
        const serviceCharge = percentOf(serviceRate, baseRent);
        const tax = percentOf(taxRate, (baseRent + serviceCharge));

        const total = (baseRent - discountAmount) + serviceCharge + tax;
        const RoomBookingCharge = baseRent;
        const AdvanceAmount = Number(values.advance_amount || 0);
        const RemainingAmount = total - AdvanceAmount;

        return {
            baseRent,
            extras: 0,
            commission_amount,
            tax,
            total_tax_rate,
            serviceCharge,
            service_charge,
            discountAmount,
            total,
            RoomBookingCharge,
            RemainingAmount
        };
    }, [billingDeps, taxConfig, percentOf, rooms.length, values.advance_amount]);

    useEffect(() => {
        if (!rooms.length) return;

        setValues((prev) => {
            const newTotal = String(billingRates.total);

            // ✅ FIX 2: Fixed camelCase typo (changed billingRates.commissionAmount to commission_amount)
            const newCommission = String(billingRates.commission_amount);

            if (
                prev.total_amount !== newTotal ||
                prev.commission_amount !== newCommission
            ) {
                return {
                    ...prev,
                    total_amount: newTotal,
                    commission_amount: newCommission,
                };
            }

            return prev;
        });
    }, [billingRates, rooms.length, setValues]);

    return { billingRates, computedRooms };
};

export default useBillingCalculation;